// Trivia Night on screen (desktop + phone): Join / Host prompt, the lobby
// (categories, 10/15/20 questions, 15/20/30 s, classic or buzzer), and in
// game the question with four big colored answer buttons, your result at the
// reveal, the standings, and — for the host — the show controls (pause,
// skip, reveal now, end, remove a player). VR uses TriviaVRPanel.

import React, { useState } from 'react';
import { tableAction } from '../gameStore';
import { JoinPrompt, SeatList, LobbyActions, HelpButton, GameOverBlock, PanelError, leaveTable } from '../kit/LobbyKit';
import { Leaderboard } from '../Leaderboard';
import type { OverlayProps } from '../types';
import { COUNTS, TIMERS, TRIVIA_CATEGORIES, DEFAULT_OPTIONS, type TriviaOptions } from '../../../../supabase/functions/games/trivia-core.ts';
import { LETTERS } from './triviaLayout';
import { categoryLabel, fmtPts, hostAnswer, hostMove, isKicked, myPick, secs, sendAnswer, standings, triviaOf, useTimeLeft, type TriviaState } from './triviaClient';
import { sfx, isMuted, setMuted, onMuteChange } from './triviaSounds';
import './TriviaOverlay.css';

function useMuted() {
  const [m, setM] = useState(isMuted());
  React.useEffect(() => onMuteChange(setM), []);
  return m;
}

function MuteButton() {
  const m = useMuted();
  return (
    <button className="gx-sp-btn gx-sp-btn--ghost gx-sp-help" onClick={() => setMuted(!m)} aria-pressed={m} aria-label={m ? 'Unmute sounds' : 'Mute sounds'} title={m ? 'Unmute sounds' : 'Mute sounds'}>
      {m ? '🔇' : '🔊'}
    </button>
  );
}

// ---- lobby -----------------------------------------------------------------------

function OptionsEditor({ pid, entry, options, canEdit }: { pid: string; entry: OverlayProps['entry']; options: TriviaOptions; canEdit: boolean }) {
  const set = (patch: Partial<TriviaOptions>) => void tableAction(pid, 'configure', { options: patch });
  const cats = options.categories;
  const toggle = (id: string) => {
    const next = cats.includes(id) ? cats.filter((c) => c !== id) : [...cats, id];
    set({ categories: next });
  };
  const dis = !canEdit || entry.busy;
  return (
    <div className="gx-tv-opts">
      <div className="gx-tv-opt-label">Categories</div>
      <div className="gx-tv-chips" role="group" aria-label="Categories">
        <button className={`gx-tv-chip ${cats.length === 0 ? 'is-on' : ''}`} disabled={dis} aria-pressed={cats.length === 0} onClick={() => set({ categories: [] })}>Mix of everything</button>
        {TRIVIA_CATEGORIES.map((c) => (
          <button key={c.id} className={`gx-tv-chip ${cats.includes(c.id) ? 'is-on' : ''}`} disabled={dis} aria-pressed={cats.includes(c.id)} onClick={() => toggle(c.id)}>{c.label}</button>
        ))}
      </div>
      <div className="gx-tv-row">
        <Segment label="Questions" values={COUNTS} value={options.count} dis={dis} onPick={(count) => set({ count })} />
        <Segment label="Timer" values={TIMERS} value={options.timer} dis={dis} fmt={(n) => `${n}s`} onPick={(timer) => set({ timer })} />
      </div>
      <div className="gx-tv-row">
        <div className="gx-tv-seg" role="group" aria-label="Mode">
          <span className="gx-tv-opt-label">Mode</span>
          <div className="gx-tv-seg-btns">
            <button className={options.mode === 'classic' ? 'is-on' : ''} disabled={dis} aria-pressed={options.mode === 'classic'} onClick={() => set({ mode: 'classic' })}>Classic</button>
            <button className={options.mode === 'buzzer' ? 'is-on' : ''} disabled={dis} aria-pressed={options.mode === 'buzzer'} onClick={() => set({ mode: 'buzzer' })}>Buzzer</button>
          </div>
        </div>
      </div>
      <p className="gx-sp-note">
        {options.mode === 'buzzer' ? 'Buzzer: the first right answer scores most (1000, 800, 650…); a wrong buzz costs 200.' : 'Classic: a right answer scores 500–1000 — the faster, the more.'}
        {!canEdit && ' The host picks the settings.'}
      </p>
    </div>
  );
}

function Segment<N extends number>({ label, values, value, dis, onPick, fmt }: { label: string; values: readonly N[]; value: number; dis: boolean; onPick: (n: N) => void; fmt?: (n: N) => string }) {
  return (
    <div className="gx-tv-seg" role="group" aria-label={label}>
      <span className="gx-tv-opt-label">{label}</span>
      <div className="gx-tv-seg-btns">
        {values.map((n) => <button key={n} className={value === n ? 'is-on' : ''} disabled={dis} aria-pressed={value === n} onClick={() => onPick(n)}>{fmt ? fmt(n) : n}</button>)}
      </div>
    </div>
  );
}

function TriviaLobby(props: OverlayProps) {
  const { def, pid, entry, table, isHost, onHelp, worldId } = props;
  const [board, setBoard] = useState(false);
  const st = table.state;
  const options: TriviaOptions = { ...DEFAULT_OPTIONS, ...(st.options ?? {}) };
  return (
    <div className="gx-sp gx-sp--panel gx-tv" role="region" aria-label="Trivia Night lobby">
      <div className="gx-sp-head">
        <span className="gx-sp-prompt-title"><span className="gx-sp-spade">{def.glyph}</span> {def.label}{isHost ? ' — you host' : ''}</span>
        <div className="gx-sp-head-actions">
          <button className={`gx-sp-btn gx-sp-btn--ghost ${board ? 'is-on' : ''}`} onClick={() => setBoard((v) => !v)} aria-pressed={board}>Leaderboard</button>
          <button className="gx-sp-btn gx-sp-btn--ghost" onClick={() => leaveTable(pid)}>{isHost ? 'Stop hosting' : 'Leave'}</button>
        </div>
      </div>
      {board ? <Leaderboard kind={def.kind} worldId={worldId} compact title={false} limit={8} /> : <>
        <OptionsEditor pid={pid} entry={entry} options={options} canEdit={isHost || !st.host} />
        <SeatList {...props} />
      </>}
      <LobbyActions {...props} />
      <p className="gx-sp-note">
        {isHost ? 'As host you run the show and don’t score. ' : st.host ? '' : 'No host? The game runs itself — anyone who joined can start. '}
        Up to {def.maxPlayers} players; latecomers can jump in mid-game. <HelpButton def={def} onHelp={onHelp} />
      </p>
      <PanelError entry={entry} />
    </div>
  );
}

// ---- in game ---------------------------------------------------------------------

function AnswerGrid({ g, pid, entry, pick, hostKnows, canAnswer }: {
  g: TriviaState; pid: string; entry: OverlayProps['entry']; pick: number | null; hostKnows: number | null; canAnswer: boolean;
}) {
  const rv = g.phase === 'reveal' ? g.reveal : null;
  return (
    <div className={`gx-tv-grid ${rv ? 'is-reveal' : ''}`} role="group" aria-label="Answers">
      {g.question!.choices.map((c, i) => {
        const right = rv ? !rv.voided && rv.correct === i : hostKnows === i;
        const cls = ['gx-tv-ans', `gx-tv-ans--${i}`, pick === i ? 'is-picked' : '', right ? 'is-right' : '', (rv || pick !== null) && !right && pick !== i ? 'is-dim' : ''].join(' ');
        return (
          <button key={i} className={cls} disabled={!canAnswer || pick !== null || entry.busy}
            aria-label={`${LETTERS[i]}: ${c}${right && rv ? ' (right answer)' : ''}`}
            onClick={() => { sfx.buzz(); void sendAnswer(pid, g.qNo, i); }}>
            <span className="gx-tv-ans-letter">{LETTERS[i]}</span>
            <span className="gx-tv-ans-text">{c}</span>
            {rv && <span className="gx-tv-ans-count">{rv.counts[i] ?? 0}</span>}
            {!rv && hostKnows === i && <span className="gx-tv-ans-count">answer</span>}
          </button>
        );
      })}
    </div>
  );
}

function StandingsList({ g, seats, seat, limit = 10 }: { g: TriviaState; seats: (unknown | null)[]; seat: number; limit?: number }) {
  const rows = standings(g, g.phase === 'over' ? undefined : seats);
  const shown = rows.slice(0, limit);
  const me = rows.find((r) => r.seat === seat);
  return (
    <ol className="gx-tv-standings">
      {shown.map((r) => (
        <li key={r.seat} className={r.seat === seat ? 'is-me' : ''}>
          <span className="gx-tv-rank">{r.rank === 1 ? '🏆' : r.rank}</span>
          <span className="gx-tv-name">{r.name}{r.seat === seat ? ' (you)' : ''}</span>
          <span className="gx-tv-pts">{fmtPts(r.score)}</span>
        </li>
      ))}
      {me && !shown.includes(me) && (
        <li className="is-me"><span className="gx-tv-rank">{me.rank}</span><span className="gx-tv-name">{me.name} (you)</span><span className="gx-tv-pts">{fmtPts(me.score)}</span></li>
      )}
    </ol>
  );
}

function HostControls({ g, pid, entry, table }: { g: TriviaState; pid: string; entry: OverlayProps['entry']; table: OverlayProps['table'] }) {
  const [players, setPlayers] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  if (g.phase === 'over') return null;
  const paused = g.pausedLeft !== null;
  const seated = table.state.seats.map((s, i) => ({ s, i })).filter(({ s }) => s && !(s.userId && g.kicked.includes(s.userId)));
  return (
    <div className="gx-tv-host">
      <div className="gx-tv-host-title">Host controls</div>
      <div className="gx-sp-actions">
        <button className="gx-sp-btn" disabled={entry.busy} onClick={() => void hostMove(pid, { type: paused ? 'resume' : 'pause' })}>{paused ? '▶ Resume' : '❚❚ Pause'}</button>
        <button className="gx-sp-btn" disabled={entry.busy} onClick={() => void hostMove(pid, { type: 'skip' })}>{g.phase === 'question' ? 'Skip question' : 'Next ▸'}</button>
        {g.phase === 'question' && <button className="gx-sp-btn" disabled={entry.busy} onClick={() => void hostMove(pid, { type: 'reveal' })}>Reveal now</button>}
        {confirmEnd
          ? <button className="gx-sp-btn gx-tv-danger" disabled={entry.busy} onClick={() => { setConfirmEnd(false); void hostMove(pid, { type: 'end' }); }}>Really end?</button>
          : <button className="gx-sp-btn" disabled={entry.busy} onClick={() => setConfirmEnd(true)}>End game</button>}
        <button className={`gx-sp-btn gx-sp-btn--ghost ${players ? 'is-on' : ''}`} aria-pressed={players} onClick={() => setPlayers((v) => !v)}>Players ({seated.length})</button>
      </div>
      {players && (
        <ul className="gx-tv-kick">
          {seated.map(({ s, i }) => (
            <li key={i}>
              <span>{s!.name} <small>{fmtPts(g.scores[i] ?? 0)}</small></span>
              <button className="gx-sp-link" disabled={entry.busy} onClick={() => void hostMove(pid, { type: 'kick', seat: i })}>Remove</button>
            </li>
          ))}
          {!seated.length && <li className="gx-sp-note">Nobody yet</li>}
        </ul>
      )}
    </div>
  );
}

export function TriviaOverlay(props: OverlayProps) {
  const { def, pid, entry, table, seat, isHost, onHelp } = props;
  const st = table.state;
  const g = triviaOf(table);
  const left = useTimeLeft(st.phase === 'lobby' ? null : g);

  if (seat < 0 && !isHost) return <JoinPrompt {...props} />;
  if (st.phase === 'lobby' || !g) return <TriviaLobby {...props} />;

  const kicked = seat >= 0 && isKicked(table, seat);
  const pick = myPick(g, entry.priv);
  const known = isHost ? hostAnswer(g, entry.priv) : null;
  const rows = standings(g, st.seats);
  const me = rows.find((r) => r.seat === seat);
  const sLeft = left === null ? null : Math.ceil(left / 1000);
  const rv = g.phase === 'reveal' ? g.reveal : null;
  const myGain = rv && seat >= 0 ? rv.gains[seat] : undefined;
  const myRevealPick = rv && seat >= 0 ? rv.picks[seat] : undefined;

  let title: string;
  if (g.phase === 'intro') title = 'Get ready!';
  else if (g.phase === 'scores') title = 'Standings';
  else if (g.phase === 'over') title = 'Final scores';
  else title = `Question ${g.qNo}/${g.total}`;
  const sub = [(g.phase === 'question' || g.phase === 'reveal') && g.question ? categoryLabel(g.question.category) : '',
    me ? `You: ${fmtPts(me.score)} pts (#${me.rank})` : isHost ? 'You’re hosting' : ''].filter(Boolean).join(' · ');

  let result: React.ReactNode = null;
  if (rv && seat >= 0 && !kicked) {
    if (rv.voided) result = <span className="gx-tv-result">Thrown out — no points</span>;
    else if (myRevealPick === undefined) result = <span className="gx-tv-result">No answer — it was {LETTERS[rv.correct]}</span>;
    else if (myRevealPick === rv.correct) result = <span className="gx-tv-result is-right">Right! +{fmtPts(myGain ?? 0)}{rv.fastest?.seat === seat ? ' — fastest!' : ''}</span>;
    else result = <span className="gx-tv-result is-wrong">Wrong — it was {LETTERS[rv.correct]}{myGain ? ` (${fmtPts(myGain)})` : ''}</span>;
  }

  return (
    <div className={`gx-sp gx-sp--panel gx-sp--game gx-tv ${g.phase === 'question' && pick === null && !isHost ? 'is-my-turn' : ''}`} role="region" aria-label="Trivia Night">
      <div className="gx-sp-head">
        <span className="gx-tv-title">{title}</span>
        <div className="gx-sp-head-actions">
          {sLeft !== null && g.phase !== 'over' && (
            <span className={`gx-sp-timer ${sLeft <= 5 && g.phase === 'question' ? 'is-low' : ''}`} role="timer">{g.pausedLeft !== null ? '❚❚ ' : ''}{sLeft}s</span>
          )}
          <MuteButton />
          <HelpButton def={def} onHelp={onHelp} compact />
          <button className="gx-sp-btn gx-sp-btn--ghost" onClick={() => leaveTable(pid)}>{isHost ? 'Stop hosting' : 'Leave'}</button>
        </div>
      </div>
      {sub && <div className="gx-tv-sub">{sub}</div>}

      {kicked ? (
        <p className="gx-tv-status">The host removed you from this game. You can watch the screen, or leave.</p>
      ) : g.phase === 'intro' ? (
        <p className="gx-tv-status">{g.total} questions · {g.timerMs / 1000}s each · {g.mode === 'buzzer' ? 'Buzzer mode — first right answer scores most' : 'Classic — faster answers score more'}</p>
      ) : (g.phase === 'question' || g.phase === 'reveal') && g.question ? (
        <>
          <p className="gx-tv-q">{g.question.text}</p>
          <AnswerGrid g={g} pid={pid} entry={entry} pick={pick} hostKnows={known} canAnswer={!isHost && seat >= 0 && g.phase === 'question' && g.pausedLeft === null} />
          {g.phase === 'question' && (
            <p className="gx-tv-status">
              {g.pausedLeft !== null ? 'Paused by the host' : isHost ? `${g.answered.length} answered so far` : pick !== null ? 'Locked in — wait for the reveal' : 'Tap your answer'}
            </p>
          )}
          {result}
          {rv?.fastest && !rv.voided && <p className="gx-tv-status">Fastest: {g.names[rv.fastest.seat] ?? 'someone'} ({secs(rv.fastest.ms)})</p>}
        </>
      ) : (
        <StandingsList g={g} seats={st.seats} seat={seat} />
      )}

      {isHost && <HostControls g={g} pid={pid} entry={entry} table={table} />}
      {st.phase === 'over' && (isHost || !st.host) && (
        <details className="gx-tv-again">
          <summary>Settings for the rematch</summary>
          <OptionsEditor pid={pid} entry={entry} options={{ ...DEFAULT_OPTIONS, ...(st.options ?? {}) }} canEdit />
        </details>
      )}
      {st.phase === 'over' && (
        <GameOverBlock {...props} summary={me ? (me.rank === 1 && me.score > 0 ? `You win with ${fmtPts(me.score)}!` : `#${me.rank} with ${fmtPts(me.score)}`) : 'Thanks for playing'} />
      )}
      <PanelError entry={entry} />
    </div>
  );
}

export default TriviaOverlay;
